import { createAction } from '@reduxjs/toolkit';

import type { GrantCreditsInput } from '../domain/credit-ledger.model';
import type {
  DuplicateListInput,
  RequestListApprovalInput,
  SaveNotesInput,
  SetLicenceStatusInput,
  SetPrimaryContactsInput,
  SetUserArchivedInput,
  UpdateLicenceInput,
  UpdateListStatusInput,
} from '../infrastructure/licence-detail.gateway';

/**
 * "Signal" actions: intents the UI dispatches for the saga to act on. Kept out of
 * the slice's reducers: they trigger side effects, not state transitions (the saga
 * owns the resulting `load*` / mutation lifecycle transitions).
 */

/** Load the licence + its users + its lists for the given id. */
export const loadRequested = createAction<string>('licenceDetail/loadRequested');

/** Delete one of the licence's users (the account, after the page's confirmation). */
export const userDeleteRequested = createAction<string>('licenceDetail/userDeleteRequested');

/** Deactivate (archive) or reactivate one of the licence's users (#573, Administrator only). */
export const userArchiveRequested = createAction<SetUserArchivedInput>(
  'licenceDetail/userArchiveRequested',
);

/** Edit the licence's name, client category and country (Administrator only). */
export const editRequested = createAction<UpdateLicenceInput>('licenceDetail/editRequested');

/** Deactivate or reactivate the licence from the Danger zone (#582). */
export const licenceStatusRequested = createAction<SetLicenceStatusInput>(
  'licenceDetail/licenceStatusRequested',
);

/** Delete the licence for good from the Danger zone (#584); by id. */
export const licenceDeleteRequested = createAction<string>('licenceDetail/licenceDeleteRequested');

/** Save the licence's main contacts; the whole set (Administrator only, #564). */
export const primaryContactsSaveRequested = createAction<SetPrimaryContactsInput>(
  'licenceDetail/primaryContactsSaveRequested',
);

/** Delete one or several of the licence's lists, after the page's confirmation (#577). */
export const listsDeleteRequested = createAction<string[]>('licenceDetail/listsDeleteRequested');

/** Move a list to a status; also how "Send list to client" sends it (#577). */
export const listStatusRequested = createAction<UpdateListStatusInput>(
  'licenceDetail/listStatusRequested',
);

/** Ask an Invyo administrator to approve a list (#577). */
export const listApprovalRequested = createAction<RequestListApprovalInput>(
  'licenceDetail/listApprovalRequested',
);

/** Copy a list into the same licence, under the name picked in the dialog (#662). */
export const listDuplicateRequested = createAction<DuplicateListInput>(
  'licenceDetail/listDuplicateRequested',
);

/** Load who may be picked as the licence's CSM; when Edit licence opens (#583). */
export const csmCandidatesLoadRequested = createAction('licenceDetail/csmCandidatesLoadRequested');

/** Load the client category list, each time Edit licence opens (#604). */
export const clientCategoriesLoadRequested = createAction(
  'licenceDetail/clientCategoriesLoadRequested',
);

/** Add a client category from the Edit licence select (Administrator only, #604). */
export const clientCategoryAddRequested = createAction<string>(
  'licenceDetail/clientCategoryAddRequested',
);

/** Load who an approval can be asked of; once, when the dialog first opens (#577). */
export const approversLoadRequested = createAction('licenceDetail/approversLoadRequested');

/** Load the contract periods and journal (Overview tab mount, and after an edit, #750). */
export const contractHistoryLoadRequested = createAction<string>('licenceDetail/contractHistoryLoadRequested');

/** Load the licence's internal notes (dispatched when the Notes tab mounts, #605). */
export const notesLoadRequested = createAction<string>('licenceDetail/notesLoadRequested');

/** Save the licence's internal notes; the whole block (#605). */
export const notesSaveRequested = createAction<SaveNotesInput>('licenceDetail/notesSaveRequested');

/** Load the credit balance + ledger (dispatched when the Credits tab mounts). */
export const creditsLoadRequested = createAction<string>('licenceDetail/creditsLoadRequested');

/** Grant credits to the licence pool (admin only; the backend enforces it too). */
export const grantCreditsRequested = createAction<GrantCreditsInput>(
  'licenceDetail/grantCreditsRequested',
);

/** Load the newest page of the licence's activity feed (dispatched when the Overview mounts). */
export const activityLoadRequested = createAction<string>('licenceDetail/activityLoadRequested');

/**
 * Load the next, older page of the feed; the card dispatches it as its bottom comes
 * into view. The saga reads the licence and the cursor from the store, and ignores
 * the signal when there is nothing more or a page is already on its way.
 */
export const activityMoreRequested = createAction('licenceDetail/activityMoreRequested');
